import { useQuery } from '@tanstack/react-query';

import type { AuthUser } from '@/api/auth';
import { chatApi, curatorOf, type ChatMessage, type ChatSession } from '@/api/chat';
import { teacherIdOf, userSections } from '@/lib/studentFields';
import { useAuthStore } from '@/stores/auth';

import { useHasFeature } from './useHasFeature';

/**
 * Показывать ли ученику входы к куратору: «Написать куратору», «Спросить куратора», значок чата.
 *
 * ⚠️ РЕШЕНИЕ ВЛАДЕЛЬЦА 14.09.2026 (Apple 3.1.3(f)): нет куратора — нет и входа в чат. Не
 * «заблокировано», а просто нет. Раньше эти входы рисовались всем без проверки, и ученик без
 * права `chat` нажимал «Написать куратору» — и молча возвращался на главную.
 *
 * Два условия, оба обязательны:
 *  - право `chat` в наборе ученика (без него и сервер чата не пускает);
 *  - в диалогах есть куратор (`teacher_id`, раньше `curator_id` — `teacherIdOf`). Право бывает и у платного ученика, которому группу
 *    ещё не назначили, — ему незачем «Написать куратору», которого нет.
 *
 * Признака «есть куратор» в `/auth/me` нет (задача B3), поэтому смотрим список диалогов. Его
 * запрашиваем только ученику и только при пришедшем праве `chat` — `canAskStudentChat` ниже.
 */
export function shouldShowCuratorEntry(canChat: boolean, sessions?: ChatSession[]): boolean {
  if (!canChat) return false;
  return Boolean(teacherIdOf(curatorOf(sessions)));
}

/**
 * Можно ли спрашивать `GET /student/chat` (техдолг 295).
 *
 * ⚠️ СТРОЖЕ, ЧЕМ `useHasFeature('chat')`. Тот безопасен к блокировке: сотруднику — всегда да, учётке без набора
 * прав — тоже да. Для показа разделов это правильно, для запроса — нет, и оба «да» стоили лишних 403:
 *  - куратор на разборе урока ученика (`LessonSidebar`) слал запрос чата ученика от своего имени — служба
 *    отвечала 403 по роли, плюс повтор; 23.09.2026 таких было 1 613;
 *  - ответ входа кладёт учётку без `sections`, и главная успевала спросить чат раньше, чем `/auth/me` принесёт
 *    настоящий набор (разбор CHECK-2026-09-24-IMPORT-IELTS-CHAT.md §3.3).
 *
 * Поэтому: только ученик, и только когда набор прав пришёл с сервера. Учётка, сохранённая после прошлого
 * `/auth/me`, считается пришедшей — повторного `/auth/me` при каждом запуске не ждём.
 *
 * Сознательно: `/auth/me` ученика без обоих имён набора — ошибка сервера (student-api.md §4.1), и тогда входов
 * в чат не будет. Показывать их по «безопасному» правилу значило бы вернуть те же 403.
 */
export function canAskStudentChat(user: AuthUser | null): boolean {
  // ⚠️ ПРИЛОЖЕНИЕ — ТО ЖЕ ПРАВИЛО, ЧТО И САЙТ (решение владельца 02.10.2026, сборка 1.1.1): чат платнику по
  // разделу `chat`. В сборке 61 здесь стояло «в приложении — нет» (решение 26.09.2026). Бесплатник (`lessons,
  // vocab`) по этому правилу не делает к chat-service ни одного запроса: ни значка, ни «Спросить куратора».
  if (!user || user.role !== 'student') return false;
  const sections = userSections(user); // `sections`, раньше `features`
  if (sections == null) return false; // набор ещё не пришёл — ждём /auth/me
  return sections.includes('*') || sections.includes('chat');
}

/** Запрос диалогов ученика — один на все экраны, с условием `canAskStudentChat`. */
export function studentChatsQuery(user: AuthUser | null) {
  return {
    queryKey: ['student', 'chats'] as const,
    queryFn: () => chatApi.listMyChats('student'),
    enabled: canAskStudentChat(user),
  };
}

export function useCuratorChat(): {
  canChat: boolean;
  curatorChat?: ChatSession;
  showCuratorEntry: boolean;
} {
  const canChat = useHasFeature('chat');
  const user = useAuthStore((s) => s.user);
  const chats = useQuery(studentChatsQuery(user));
  const curatorChat = canChat ? curatorOf(chats.data) : undefined;
  return { canChat, curatorChat, showCuratorEntry: shouldShowCuratorEntry(canChat, chats.data) };
}

/** Сколько последних сообщений смотрим ради точки на значке. */
export const UNREAD_WINDOW = 20;

/** Непрочитанные учеником: чужие сообщения без отметки «прочитано». */
export function countUnread(messages: ChatMessage[] | undefined, meId: string | undefined): number {
  if (!meId) return 0;
  return (messages ?? []).filter((m) => m.sender_id !== meId && !m.is_read).length;
}

export const chatUnreadKey = (roomId: string) => ['student', 'chat-unread', roomId] as const;

/**
 * Есть ли у ученика непрочитанное от куратора — для точки на значке чата на главной (03.10.2026, сборка 1.1.1).
 *
 * ⚠️ ЗАЧЕМ. Пуша «сообщение куратора» в 1.1.1 нет (решение владельца 02.10.2026): об ответе ученик узнаёт только
 * по этой точке. Поэтому запрос идёт ПРИ КАЖДОМ открытии главной (`refetchOnMount: 'always'`, мимо общих 30 секунд
 * свежести) и при возврате приложения из фона (`lib/freshness.ts` перезапрашивает активные запросы).
 *
 * ⚠️ ПОЧЕМУ НЕ `unread_count` ИЗ `GET /student/chat`. Сервер ученику его не считает: `GetStudentChat` в
 * chat-service заполняет только имена, поле всегда 0 (у списка куратора — считается). Пока так, непрочитанные
 * считаем сами по последним сообщениям диалога; придёт ненулевое поле — верим ему.
 *
 * Запрос — только при диалоге с назначенным куратором: бесплатнику и платнику без куратора он не уходит.
 */
export function useChatUnread(): number {
  const user = useAuthStore((s) => s.user);
  const { curatorChat, showCuratorEntry } = useCuratorChat();
  const roomId = showCuratorEntry ? curatorChat?.id : undefined;
  const unread = useQuery({
    queryKey: chatUnreadKey(roomId ?? ''),
    queryFn: async () => countUnread(await chatApi.getHistory(roomId!, 0, UNREAD_WINDOW), user?.id),
    enabled: Boolean(roomId) && canAskStudentChat(user),
    refetchOnMount: 'always',
  });
  if (!roomId) return 0;
  return Math.max(curatorChat?.unread_count ?? 0, unread.data ?? 0);
}
